import { useState, useEffect } from "react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { DatePicker } from "../ui/DatePicker";
import { Download, AlertCircle, CheckCircle } from "lucide-react";
import { instructorService } from "../../services/instructorService";
import {
  convertToCSV,
  downloadCSV,
  formatInstructorForCSV,
  generateTimestampedFilename,
} from "../../utils/csvExport";
import { useAuth } from "../../contexts/AuthContext";

interface ExportInstructorsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  initialSearchTerm?: string;
  initialStatus?: "all" | "approved" | "pending" | "rejected";
}

const INSTRUCTOR_CSV_HEADERS: { key: string; label: string }[] = [
  { key: "instructorId", label: "Instructor ID" },
  { key: "username", label: "Username" },
  { key: "firstName", label: "First Name" },
  { key: "lastName", label: "Last Name" },
  { key: "fullName", label: "Full Name" },
  { key: "email", label: "Email" },
  { key: "phoneNumber", label: "Phone Number" },
  { key: "isEmailVerified", label: "Email Verified" },
  { key: "professionalTitle", label: "Professional Title" },
  { key: "approvalStatus", label: "Approval Status" },
  { key: "expertiseAreas", label: "Expertise Areas" },
  { key: "yearsOfExperience", label: "Years of Experience" },
  { key: "biography", label: "Biography" },
  { key: "createdAt", label: "Created At" },
  { key: "updatedAt", label: "Updated At" },
];

export function ExportInstructorsDialog({
  isOpen,
  onClose,
  initialSearchTerm,
  initialStatus,
}: ExportInstructorsDialogProps) {
  const { hasPermission } = useAuth();

  const [searchTerm, setSearchTerm] = useState(initialSearchTerm ?? "");
  const [status, setStatus] = useState<
    "all" | "approved" | "pending" | "rejected"
  >(initialStatus ?? "all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [recordCount, setRecordCount] = useState<number | null>(null);
  const [isLoadingCount, setIsLoadingCount] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [notification, setNotification] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const buildFilterParams = () => {
    const params: Record<string, string> = {};

    if (status !== "all") {
      params.approvalStatus = status;
    }

    if (searchTerm) {
      params.search = searchTerm;
    }

    if (fromDate) {
      params.fromDate = fromDate;
    }

    if (toDate) {
      params.toDate = toDate;
    }

    return params;
  };

  // Fetch record count when filters change (or dialog opens)
  useEffect(() => {
    if (isOpen) {
      fetchRecordCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, searchTerm, fromDate, toDate, isOpen]);

  const fetchRecordCount = async () => {
    setIsLoadingCount(true);
    try {
      const params = buildFilterParams();
      const response = await instructorService.getInstructorsCount(params);
      setRecordCount(response.total || 0);
    } catch (error) {
      console.error("Failed to fetch instructors record count:", error);
      setRecordCount(0);
    } finally {
      setIsLoadingCount(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    setNotification(null);

    try {
      const params = buildFilterParams();
      const response = await instructorService.exportInstructors(params);
      const instructors = response.instructors || [];

      if (instructors.length === 0) {
        setNotification({
          type: "error",
          message: "No records found matching the selected filters.",
        });
        setIsExporting(false);
        return;
      }

      const formattedData = instructors.map(formatInstructorForCSV);
      const csvContent = convertToCSV(formattedData, INSTRUCTOR_CSV_HEADERS);

      const filename = generateTimestampedFilename("instructors");
      downloadCSV(csvContent, filename);

      setNotification({
        type: "success",
        message: `Successfully exported ${instructors.length} instructor(s) to ${filename}`,
      });

      setTimeout(() => {
        onClose();
        setNotification(null);
      }, 2000);
    } catch (error: any) {
      console.error("Export failed:", error);
      setNotification({
        type: "error",
        message: error.message || "Failed to export instructors. Please try again.",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleReset = () => {
    setSearchTerm("");
    setStatus("all");
    setFromDate("");
    setToDate("");
  };

  // Defense-in-depth: the parent page already gates the trigger button,
  // but keep an inner check so the dialog stays inert if mounted directly.
  if (!hasPermission("analytics:export")) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Export Instructors to CSV">
      <div className="space-y-6 py-2">
        {/* Search Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Search (optional)
          </label>
          <Input
            type="text"
            placeholder="Search by name, email, or expertise..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-9 text-sm"
          />
        </div>

        {/* Date Range Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Joined Date Range
          </label>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <DatePicker
                value={fromDate}
                onChange={(val) => setFromDate(val)}
                placeholder="From date"
                max={toDate || undefined}
              />
            </div>
            <span className="text-gray-400">to</span>
            <div className="flex-1">
              <DatePicker
                value={toDate}
                onChange={(val) => setToDate(val)}
                placeholder="To date"
                min={fromDate || undefined}
              />
            </div>
          </div>
        </div>

        {/* Status Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Status</label>
          <div className="flex gap-2">
            {[
              { id: "all", label: "All" },
              { id: "approved", label: "Approved" },
              { id: "pending", label: "Pending" },
              { id: "rejected", label: "Rejected" },
            ].map((s) => (
              <button
                key={s.id}
                onClick={() =>
                  setStatus(s.id as "all" | "approved" | "pending" | "rejected")
                }
                className={`flex-1 px-3 py-2 text-xs rounded-lg border transition-all ${
                  status === s.id
                    ? "bg-[#102040] text-white border-[#102040]"
                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Record Count Preview */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              Records to Export:
            </span>
            <span className="text-lg font-bold text-[#102040]">
              {isLoadingCount ? (
                <div className="animate-spin inline-block w-5 h-5 border-2 border-current border-t-transparent rounded-full" />
              ) : recordCount !== null ? (
                recordCount.toLocaleString()
              ) : (
                "—"
              )}
            </span>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div
            className={`flex items-center gap-2 p-3 rounded-lg ${
              notification.type === "success"
                ? "bg-green-50 text-green-700 border border-green-200"
                : "bg-red-50 text-red-700 border border-red-200"
            }`}
          >
            {notification.type === "success" ? (
              <CheckCircle size={18} />
            ) : (
              <AlertCircle size={18} />
            )}
            <span className="text-sm">{notification.message}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between gap-3 pt-6 border-t border-gray-100">
          <Button
            variant="outline"
            onClick={handleReset}
            className="px-6 text-red-600 hover:bg-red-50 hover:border-red-100"
            disabled={isExporting}
          >
            Reset Filters
          </Button>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="px-6"
              disabled={isExporting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleExport}
              className="px-8 bg-[#102040] hover:bg-[#081a30] flex items-center gap-2"
              disabled={isExporting || recordCount === 0}
            >
              {isExporting ? (
                <>
                  <div className="animate-spin inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download size={16} />
                  Export CSV
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}